import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiClock, FiPlay, FiBookOpen, FiMic } from 'react-icons/fi';
import ReminderEditor from './ReminderEditor';
import ColorThemePicker from './ColorThemePicker';

const PRESETS = [
  { label: 'Short Talk', minutes: 2, icon: FiMic },
  { label: 'Quick Quiz', minutes: 5, icon: FiBookOpen },
  { label: 'Standard Test', minutes: 20, icon: FiBookOpen },
  { label: 'Full Exam', minutes: 60, icon: FiBookOpen },
];

const SetupScreen = ({ onStart }) => {
  const [customMinutes, setCustomMinutes] = useState(15);
  const [customSeconds, setCustomSeconds] = useState(0);
  const [reminders, setReminders] = useState([]);
  const [themeColor, setThemeColor] = useState('#4a8ccc');

  const startCustomTimer = () => {
    const totalSeconds = customMinutes * 60 + customSeconds;

    const validReminders = reminders
      .filter((reminder) => reminder.message.trim())
      .map((reminder) => {
        const minutes = Math.max(0, Number(reminder.minutes) || 0);
        const secondsPart = Math.min(
          59,
          Math.max(0, Number(reminder.secondsPart) || 0)
        );
        const seconds = Math.min(
          totalSeconds,
          minutes * 60 + secondsPart
        );

        return { ...reminder, minutes, secondsPart, seconds };
      })
      .filter((reminder) => reminder.seconds >= 0)
      .sort((a, b) => b.seconds - a.seconds);

    onStart(totalSeconds, validReminders, themeColor);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative z-10 mx-auto w-full max-w-3xl rounded-3xl border border-[#c9e1f3] bg-white/85 p-5 shadow-xl backdrop-blur-md sm:p-8"
    >
      <div className="mb-8 text-center">
        <h1 className="mb-3 flex items-center justify-center gap-3 text-4xl font-bold text-[#245b86]">
          <SafeIcon icon={FiClock} className="text-[#4a8ccc]" />
          Focus Timer
        </h1>
        <p className="text-lg text-[#4b789b]">
          Create a calm, focused session for tests, talks, or presentations.
        </p>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4">
        {PRESETS.map((preset) => (
          <button
            key={preset.minutes}
            onClick={() => onStart(preset.minutes * 60, [], themeColor)}
            className="group flex flex-col items-center justify-center rounded-2xl border border-[#c9e1f3] bg-[#edf6fc] p-5 transition hover:bg-[#dceefa]"
          >
            <SafeIcon
              icon={preset.icon}
              className="mb-3 text-3xl text-[#4a8ccc] transition-transform group-hover:scale-110"
            />
            <span className="font-semibold text-[#245b86]">
              {preset.minutes} Min
            </span>
            <span className="mt-1 text-xs text-[#5d87a7]">
              {preset.label}
            </span>
          </button>
        ))}
      </div>

      <div className="mb-5 rounded-2xl border border-[#c9e1f3] bg-[#edf6fc]/70 p-5">
        <p className="mb-3 text-sm font-medium text-[#376b92]">
          Custom session length
        </p>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <label className="flex-1 text-sm text-[#376b92]">
            Minutes
            <input
              type="number"
              min="0"
              max="180"
              value={customMinutes}
              onChange={(event) =>
                setCustomMinutes(
                  Math.min(180, Math.max(0, Number(event.target.value) || 0))
                )
              }
              className="mt-2 w-full rounded-xl border-2 border-[#c9e1f3] bg-white p-3 text-2xl text-[#245b86] outline-none transition focus:border-[#4a8ccc]"
            />
          </label>

          <label className="text-sm text-[#376b92]">
            Seconds
            <input
              type="number"
              min="0"
              max="59"
              value={customSeconds}
              onChange={(event) =>
                setCustomSeconds(
                  Math.min(59, Math.max(0, Number(event.target.value) || 0))
                )
              }
              className="mt-2 w-full rounded-xl border-2 border-[#c9e1f3] bg-white p-3 text-2xl text-[#245b86] outline-none transition focus:border-[#4a8ccc]"
            />
          </label>

          <button
            onClick={startCustomTimer}
            disabled={customMinutes === 0 && customSeconds === 0}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#4a8ccc] px-8 py-4 text-lg font-bold text-white transition hover:bg-[#397bbd] hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon icon={FiPlay} />
            Start
          </button>
        </div>
      </div>

      <div className="space-y-5">
        <ReminderEditor reminders={reminders} onChange={setReminders} />
        <ColorThemePicker color={themeColor} onChange={setThemeColor} />
      </div>
    </motion.div>
  );
};

export default SetupScreen;